import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Package, X, CheckCircle, AlertCircle, Lock, Unlock, Clock, AlertTriangle, Save, Loader } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

const KNOWN_SERVICES = [
    { key: 'geoagent',       name: 'GeoAgent',       description: 'Territory planning, daily briefings, visit routing.' },
    { key: 'lead_concierge', name: 'Lead Concierge',  description: 'Lead list × customer master dedupe & matching.' },
];

const TIERS    = ['free', 'starter', 'pro', 'enterprise'];
const STATUSES = ['active', 'trial', 'suspended', 'canceled'];
const DESTRUCTIVE_STATUSES = new Set(['suspended', 'canceled']);
const ENTITLED_STATUSES    = new Set(['active', 'trial']);

const STATUS_BADGE = {
    active:    'bg-emerald-900/40 text-emerald-300 border-emerald-700',
    trial:     'bg-blue-900/40 text-blue-300 border-blue-700',
    suspended: 'bg-amber-900/40 text-amber-300 border-amber-700',
    canceled:  'bg-gray-800 text-gray-400 border-gray-600',
};

const extractError = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (detail.message) return detail.message;
    return fallback;
};

const destructiveTransitions = (diffs) =>
    diffs.filter(
        (d) => d.statusChanged &&
               ENTITLED_STATUSES.has(d.saved.status) &&
               DESTRUCTIVE_STATUSES.has(d.draft.status),
    );

// Drag-by-header hook — returns { pos, headerProps }
function useDraggable(enabled) {
    const [pos, setPos] = useState(null); // null = centred (CSS default)
    const dragging = useRef(false);
    const offset   = useRef({ x: 0, y: 0 });

    const onMouseDown = (e) => {
        if (!enabled) return;
        if (e.target.closest('button')) return; // don't hijack close button
        dragging.current = true;
        const rect = e.currentTarget.closest('[data-draggable]').getBoundingClientRect();
        offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        document.body.style.userSelect = 'none';

        const onMove = (ev) => {
            if (!dragging.current) return;
            setPos({ x: ev.clientX - offset.current.x, y: ev.clientY - offset.current.y });
        };
        const onUp = () => {
            dragging.current = false;
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    return { pos, headerProps: { onMouseDown } };
}

const OrgServices = ({ currentUser, onClose }) => {
    const [saved,       setSaved]       = useState({});
    const [draft,       setDraft]       = useState({});
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState(null);
    const [success,     setSuccess]     = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [history,     setHistory]     = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const isAdmin    = currentUser?.role === 'admin';
    const isDesktop  = typeof window !== 'undefined' && window.innerWidth >= 768;
    const { pos, headerProps } = useDraggable(isDesktop);

    const loadSubs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${API_BASE_URL}/api/admin/org-subscriptions`);
            const byKey = {};
            (res.data || []).forEach((row) => { byKey[row.service_key] = row; });
            KNOWN_SERVICES.forEach((svc) => {
                if (!byKey[svc.key]) {
                    byKey[svc.key] = { service_key: svc.key, tier: 'free', status: 'canceled', expires_at: null };
                }
            });
            setSaved(byKey);
            setDraft(JSON.parse(JSON.stringify(byKey)));
        } catch (err) {
            setError(extractError(err, 'Failed to load subscriptions'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/org-subscriptions/history`);
            setHistory(res.data || []);
            setShowHistory(true);
        } catch {
            setError('Failed to load history');
        }
    };

    useEffect(() => {
        if (!isAdmin) { setError('Unauthorized: Admin access required'); setLoading(false); return; }
        loadSubs();
    }, [isAdmin, loadSubs]);

    const diffs = useMemo(() => {
        return KNOWN_SERVICES.map((svc) => {
            const s = saved[svc.key];
            const d = draft[svc.key];
            if (!s || !d) return null;
            const statusChanged = s.status !== d.status;
            const tierChanged   = s.tier   !== d.tier;
            if (!statusChanged && !tierChanged) return null;
            return { service_key: svc.key, service_name: svc.name, saved: s, draft: d, statusChanged, tierChanged };
        }).filter(Boolean);
    }, [saved, draft]);

    const dirtyCount      = diffs.length;
    const destructive     = destructiveTransitions(diffs);
    const requiresGate    = destructive.length > 0;
    const gateWord        = destructive.some((d) => d.draft.status === 'canceled') ? 'CANCEL' : 'SUSPEND';
    const gateArmed       = !requiresGate || confirmText.trim().toUpperCase() === gateWord;

    const updateDraft = (serviceKey, patch) =>
        setDraft((prev) => ({ ...prev, [serviceKey]: { ...prev[serviceKey], ...patch } }));

    const discard = () => { setDraft(JSON.parse(JSON.stringify(saved))); setError(null); setSuccess(null); };

    const applyChanges = async () => {
        try {
            setSaving(true);
            setError(null);
            for (const d of diffs) {
                await axios.put(`${API_BASE_URL}/api/admin/org-subscriptions/${d.service_key}`, {
                    status: d.draft.status, tier: d.draft.tier,
                });
            }
            await loadSubs();
            setShowConfirm(false);
            setConfirmText('');
            try {
                const refreshRes = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`);
                const newToken = refreshRes.data?.access_token;
                if (newToken) { localStorage.setItem('token', newToken); localStorage.setItem('lc_jwt_token', newToken); }
            } catch { /* non-fatal */ }
            setSuccess(`${diffs.length} change${diffs.length === 1 ? '' : 's'} saved`);
            setTimeout(() => setSuccess(null), 2500);
        } catch (err) {
            setError(extractError(err, 'Save failed — some changes may not have been applied'));
            loadSubs();
        } finally {
            setSaving(false);
        }
    };

    // Panel positioning — draggable on desktop
    const panelStyle = pos && isDesktop
        ? { position: 'fixed', left: pos.x, top: pos.y, margin: 0, transform: 'none' }
        : {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div
                data-draggable
                style={panelStyle}
                className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            >
                {/* Header */}
                <div
                    {...headerProps}
                    className={`bg-gradient-to-r from-blue-900 to-purple-900 px-6 py-4 flex items-center justify-between flex-shrink-0 ${isDesktop ? 'cursor-move' : ''}`}
                >
                    <div className="flex items-center gap-3">
                        <Package className="w-6 h-6 text-blue-300" />
                        <div>
                            <h1 className="text-xl font-bold text-white">Org Services</h1>
                            <p className="text-sm text-blue-200">Enable or disable services for your organization</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Mobile banner */}
                    <div className="md:hidden p-3 bg-blue-900/40 border border-blue-700 rounded-lg text-sm text-blue-200">
                        For the best experience, manage org services on desktop.
                    </div>

                    {error && (
                        <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}
                    {success && (
                        <div className="p-3 bg-green-900/50 border border-green-700 rounded-lg flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                            <p className="text-green-300 text-sm">{success}</p>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader className="w-8 h-8 text-blue-400 animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {KNOWN_SERVICES.map((svc) => {
                                const d = draft[svc.key];
                                const s = saved[svc.key];
                                if (!d) return null;
                                const entitled = ENTITLED_STATUSES.has(d.status);
                                const dirty = s && (s.status !== d.status || s.tier !== d.tier);

                                return (
                                    <div
                                        key={svc.key}
                                        className={`bg-gray-800 border rounded-xl p-5 transition-colors ${
                                            dirty ? 'border-amber-600' : 'border-gray-700'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3 mb-4">
                                            <div className={`p-2 rounded-lg flex-shrink-0 ${entitled ? 'bg-emerald-900/40' : 'bg-gray-700'}`}>
                                                {entitled
                                                    ? <Unlock className="w-5 h-5 text-emerald-400" />
                                                    : <Lock   className="w-5 h-5 text-gray-500" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-semibold text-white">{svc.name}</h3>
                                                    <span className={`px-2 py-0.5 text-xs rounded border font-medium ${STATUS_BADGE[d.status] || STATUS_BADGE.canceled}`}>
                                                        {d.status}
                                                    </span>
                                                    {dirty && (
                                                        <span className="px-2 py-0.5 text-xs rounded bg-amber-900/40 border border-amber-700 text-amber-300">
                                                            Unsaved
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-400 mt-1">{svc.description}</p>
                                                {s?.expires_at && (
                                                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        Expires {new Date(s.expires_at).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 mb-1">Status</label>
                                                <select
                                                    value={d.status}
                                                    disabled={saving}
                                                    onChange={(e) => updateDraft(svc.key, { status: e.target.value })}
                                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
                                                >
                                                    {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-400 mb-1">Tier</label>
                                                <select
                                                    value={d.tier}
                                                    disabled={saving}
                                                    onChange={(e) => updateDraft(svc.key, { tier: e.target.value })}
                                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
                                                >
                                                    {TIERS.map((x) => <option key={x} value={x}>{x}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Sticky footer */}
                <div className="flex-shrink-0 bg-gray-900 border-t border-gray-700 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadHistory}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                        >
                            <Clock className="w-4 h-4" />
                            History
                        </button>
                        <span className="text-sm text-gray-500">
                            {dirtyCount === 0 ? 'No unsaved changes' : (
                                <span className="text-amber-400 font-medium">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={discard}
                            disabled={dirtyCount === 0 || saving}
                            className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Discard
                        </button>
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => { setConfirmText(''); setShowConfirm(true); }}
                            disabled={dirtyCount === 0 || saving}
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-opacity"
                        >
                            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirm modal */}
            {showConfirm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => !saving && setShowConfirm(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-gray-800 border border-gray-700 rounded-xl max-w-lg w-full shadow-2xl">
                        <div className="px-6 py-4 border-b border-gray-700 flex items-center gap-3">
                            {requiresGate
                                ? <AlertTriangle className="w-5 h-5 text-red-400" />
                                : <CheckCircle  className="w-5 h-5 text-blue-400" />}
                            <h3 className="text-lg font-semibold text-white">
                                Confirm change{dirtyCount === 1 ? '' : 's'}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-400">You are about to change subscriptions for your organization:</p>
                            <ul className="space-y-2">
                                {diffs.map((d) => {
                                    const wasEntitled  = ENTITLED_STATUSES.has(d.saved.status);
                                    const willEntitled = ENTITLED_STATUSES.has(d.draft.status);
                                    const losing       = wasEntitled && !willEntitled;
                                    return (
                                        <li key={d.service_key} className={`text-sm p-3 rounded-lg border ${losing ? 'border-red-700 bg-red-900/20' : 'border-gray-600 bg-gray-700/30'}`}>
                                            <div className="font-medium text-white">{d.service_name}</div>
                                            <div className="mt-1 text-xs font-mono text-gray-300">
                                                {d.saved.status} / {d.saved.tier}
                                                {' → '}
                                                <span className={losing ? 'text-red-400 font-bold' : 'text-emerald-400'}>
                                                    {d.draft.status} / {d.draft.tier}
                                                </span>
                                            </div>
                                            {losing && (
                                                <div className="mt-2 text-xs text-red-400">
                                                    ⚠ All users will immediately lose access to {d.service_name}.
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            {requiresGate && (
                                <div className="pt-2">
                                    <label className="block text-sm text-gray-300 mb-1">
                                        Type <span className="font-mono font-bold text-red-400">{gateWord}</span> to confirm:
                                    </label>
                                    <input
                                        type="text"
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        autoFocus
                                        placeholder={gateWord}
                                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-mono focus:border-red-500 focus:outline-none"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 bg-gray-900 rounded-b-xl flex justify-end gap-2">
                            <button onClick={() => setShowConfirm(false)} disabled={saving} className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 text-sm">Cancel</button>
                            <button
                                onClick={applyChanges}
                                disabled={!gateArmed || saving}
                                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed ${requiresGate ? 'bg-red-600 hover:bg-red-700' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90'}`}
                            >
                                {saving ? <Loader className="w-4 h-4 animate-spin" /> : null}
                                {saving ? 'Saving…' : 'Apply Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* History modal */}
            {showHistory && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
                    <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
                            <h3 className="text-lg font-bold text-white">Change History</h3>
                            <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-gray-700 rounded-lg">
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-3">
                            {history.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No change history yet</p>
                            ) : (
                                history.map((record, idx) => (
                                    <div key={idx} className="p-4 bg-gray-700/40 border border-gray-600 rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <span className="text-xs font-semibold text-blue-300 uppercase">{record.service_key}</span>
                                                <div className="text-xs text-gray-400 mt-0.5 font-mono">
                                                    {record.old_status}/{record.old_tier} → <span className="text-white">{record.new_status}/{record.new_tier}</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-gray-400">{new Date(record.changed_at).toLocaleString()}</div>
                                                <div className="text-xs text-gray-500">{record.changed_by_email || 'Unknown'}</div>
                                            </div>
                                        </div>
                                        {record.change_reason && (
                                            <p className="text-xs text-gray-400 italic">"{record.change_reason}"</p>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgServices;
